import { SUBSCRIPTION_PRICE_DROP_QUERY_KEY } from '@/hooks/queries/use-subscription-price-drop';
import { SUBSCRIPTION_QUERY_KEY } from '@/hooks/queries/use-subscription-query';
import { useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';

/** Value the backend puts on the return URL after the Shopify approval page. */
export const PRICE_DROP_RETURNED = 'returned';

/**
 * Handle the landing on `/settings/billing?priceDrop=returned`.
 *
 * The approval only reaches us through the Shopify webhook, so what the page
 * held before leaving is stale. Refetch the offer and the current subscription
 * once, let the page hold the card back until they land so the offer the
 * merchant just approved never flashes, then drop the parameter so a manual
 * refresh does not replay it.
 *
 * @returns whether the post-return refetch is still in flight
 */
export function useSubscriptionPriceDropReturn(): boolean {
  const [priceDrop, setPriceDrop] = useQueryState('priceDrop', parseAsString);
  const queryClient = useQueryClient();
  const [isReturning, setIsReturning] = useState(false);

  useEffect(() => {
    if (priceDrop !== PRICE_DROP_RETURNED) return;

    let cancelled = false;
    setIsReturning(true);

    void Promise.all([
      queryClient.refetchQueries({ queryKey: SUBSCRIPTION_PRICE_DROP_QUERY_KEY }),
      queryClient.refetchQueries({ queryKey: SUBSCRIPTION_QUERY_KEY }),
    ]).finally(() => {
      if (cancelled) return;
      setIsReturning(false);
      void setPriceDrop(null, { history: 'replace' });
    });

    return () => {
      cancelled = true;
    };
  }, [priceDrop, queryClient, setPriceDrop]);

  return isReturning;
}
